import React, { useState } from 'react';
import { Modal, Form, Input, DatePicker, message, Row, Col, Collapse, Select, Alert } from 'antd';
import dayjs from 'dayjs';
import Cookies from 'js-cookie';
import { EstateRole, EstateRoleInvitePayload, EstateRoleInviteResponse } from '../../types';
import { inviteToEstateRole } from '../estatelist/estateService';
import { formatSSN, SSN_RULE } from '../estatelist/ssn';
import { formatTIN, TIN_RULE } from '../estatelist/tin';
import { US_STATES } from '../estatelist/usstates';

interface InviteRoleModalProps {
  open: boolean;
  onCancel: () => void;
  /** Fired once the invite is sent, so the caller can re-read the rosters. */
  onSuccess: (response: EstateRoleInviteResponse) => void;
  estateId: string;
  /** Which roster the invite is for. Decides the wording and the endpoint. */
  role: EstateRole;
}

const ROLE_LABELS: Record<EstateRole, string> = {
  executor: 'Executor',
  lawyer: 'Lawyer',
};

/**
 * Invite somebody to act for an estate, as its executor or as its lawyer.
 *
 * One modal for both: the two roles differ in which roster they land on, not
 * in what the estate needs to know about them.
 *
 * A name and an email are all it asks for, so sending an invite stays a short
 * job. Everything the probate petition wants from whoever signs it sits behind
 * "Add their details now", collapsed: an executor who has their attorney's
 * date of birth and address to hand can enter them once, and the attorney
 * arrives at a profile that is already mostly written instead of an empty
 * form. Whatever is skipped can be filled in later, by either of them.
 *
 * None of it goes into the email. It was typed by one person about another,
 * and nothing here confirms that the person at that address is the person
 * described, so the address stays unverified and the details stay unsent.
 */
const InviteRoleModal: React.FC<InviteRoleModalProps> = ({
  open,
  onCancel,
  onSuccess,
  estateId,
  role,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState<boolean>(false);
  const label = ROLE_LABELS[role];
  const lower = label.toLowerCase();

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const token = Cookies.get('accessToken');
      if (!token) {
        message.error('Unauthorized. Please log in.');
        setLoading(false);
        onCancel();
        return;
      }

      const address = {
        addressLine1: values.addressLine1 || undefined,
        addressLine2: values.addressLine2 || undefined,
        city: values.city || undefined,
        state: values.state || undefined,
        zipCode: values.zipCode || undefined,
        county: values.county || undefined,
      };
      const hasAddress = Object.values(address).some(Boolean);

      const payload: EstateRoleInvitePayload = {
        name: values.name,
        email: values.email,
        prefix: values.prefix || undefined,
        middleName: values.middleName || undefined,
        suffix: values.suffix || undefined,
        phone: values.phone || undefined,
        dob: values.dob ? values.dob.format('YYYY-MM-DD') : undefined,
        ssn: values.ssn || undefined,
        tin: values.tin || undefined,
        citizenship: values.citizenship || undefined,
        county: values.county || undefined,
        address: hasAddress ? address : undefined,
      };

      const response = await inviteToEstateRole(estateId, role, payload, token);
      onSuccess(response);
      setLoading(false);
      form.resetFields();
      message.success(response.message);
      onCancel();
    } catch (error: any) {
      if (error?.errorFields) {
        setLoading(false);
        return; // validation, already shown on the fields
      }
      console.error(error);
      if (error.message === 'Unauthorized') {
        message.error('Unauthorized. Please log in.');
      } else {
        message.error(error.message || `Failed to invite ${lower}.`);
      }
      setLoading(false);
    }
  };

  return (
    <Modal
      title={`Add ${label}`}
      open={open}
      onOk={handleOk}
      onCancel={onCancel}
      okText={`Add ${label}`}
      cancelText="Cancel"
      confirmLoading={loading}
      destroyOnClose
      width={680}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label="Name"
          name="name"
          rules={[{ required: true, message: `Please enter the ${lower}'s name!` }]}
        >
          <Input placeholder={`Enter ${lower}'s name`} />
        </Form.Item>
        <Form.Item
          label="Email Address"
          name="email"
          rules={[
            { required: true, message: `Please enter the ${lower}'s email address!` },
            { type: 'email', message: 'Please enter a valid email address!' },
          ]}
        >
          <Input placeholder={`Enter ${lower}'s email`} />
        </Form.Item>

        <Collapse
          ghost
          items={[
            {
              key: 'details',
              label: `Add their details now (optional)`,
              children: (
                <>
                  <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="Nothing here is sent in the invitation email."
                    description={`The probate petition asks for all of this. Fill in whatever you already have and the ${lower} will not have to. They can correct any of it from their invitation link, and their email address stays unverified until they open it.`}
                  />

                  <Row gutter={16}>
                    <Col span={5}>
                      <Form.Item label="Title" name="prefix">
                        <Input placeholder="Mrs" />
                      </Form.Item>
                    </Col>
                    <Col span={11}>
                      <Form.Item label="Middle Name" name="middleName">
                        <Input placeholder="Middle name" />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item label="Suffix" name="suffix">
                        <Input placeholder="Esq, Jr" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={8}>
                      <Form.Item label="Date of Birth" name="dob">
                        <DatePicker
                          style={{ width: '100%' }}
                          format="YYYY-MM-DD"
                          placeholder="Date of birth"
                          disabledDate={(current) => current && current > dayjs().endOf('day')}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item label="Phone" name="phone">
                        <Input placeholder="Phone number" />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item label="Citizenship" name="citizenship">
                        <Input placeholder="Country of citizenship" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label="Social Security Number"
                        name="ssn"
                        normalize={formatSSN}
                        rules={[SSN_RULE]}
                        extra="Stored encrypted and shown masked."
                      >
                        <Input placeholder="123-45-6789" maxLength={11} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      {/* An executor need not be a person: a bank's trust
                          department or a trust company has a TIN instead. */}
                      <Form.Item
                        label="Taxpayer ID (TIN)"
                        name="tin"
                        normalize={formatTIN}
                        rules={[TIN_RULE]}
                        extra="For an executor that is a bank, firm or trust company."
                      >
                        <Input placeholder="12-3456789" maxLength={10} />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item label="Address Line 1" name="addressLine1">
                    <Input placeholder="Street address" />
                  </Form.Item>
                  <Form.Item label="Address Line 2" name="addressLine2">
                    <Input placeholder="Apartment, suite, etc." />
                  </Form.Item>
                  <Row gutter={16}>
                    <Col span={8}>
                      <Form.Item label="City" name="city">
                        <Input placeholder="City" />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item label="State" name="state">
                        <Select
                          allowClear
                          showSearch
                          placeholder="Select state"
                          optionFilterProp="children"
                        >
                          {US_STATES.map((state) => (
                            <Select.Option key={state.code} value={state.code}>
                              {state.name}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item label="Zip Code" name="zipCode">
                        <Input placeholder="Zip code" />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item
                    label="County"
                    name="county"
                    extra="The probate petition asks for this by name."
                  >
                    <Input placeholder="County of residence" />
                  </Form.Item>
                </>
              ),
            },
          ]}
        />
      </Form>
    </Modal>
  );
};

export default InviteRoleModal;
